import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Read and write URL query params.
 * Default (set/remove) keeps original behavior: reload via window.location.href (Telegram-safe for full redirects).
 * New helpers (setInPlace/removeInPlace/useQueryParamState) update the URL **without reload** using History API.
 */

type Updates = Record<string, string | number | boolean | null | undefined>;
type GetOptions<T = string | null> = { fallback?: T };

export function useQueryParams() {
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

  const get = useCallback(
    <T extends string | null = string | null>(key: string, opts?: GetOptions<T>): T => {
      if (!isBrowser) return (opts?.fallback ?? (null as T)) as T;
      const params = new URLSearchParams(window.location.search);
      const has = params.has(key);
      return (has ? (params.get(key) as T) : (opts?.fallback ?? (null as T))) as T;
    },
    [isBrowser]
  );

  /**
   * ORIGINAL behavior: apply updates and **reload** current page.
   * Use for flows where a hard reload is desired (e.g., after auth redirects).
   */
  const set = useCallback(
    (updates: Updates = {}) => {
      if (!isBrowser) return;
      const url = new URL(window.location.href);
      for (const [k, v] of Object.entries(updates)) {
        if (v === undefined || v === null || v === '') url.searchParams.delete(k);
        else url.searchParams.set(k, String(v));
      }
      // Telegram-safe navigation within the same window (hard reload)
      window.location.href = url.toString();
    },
    [isBrowser]
  );

  /**
   * ORIGINAL behavior: remove keys and **reload** current page.
   */
  const remove = useCallback(
    (keys: string[] = []) => {
      if (!isBrowser) return;
      const url = new URL(window.location.href);
      for (const k of keys) url.searchParams.delete(k);
      window.location.href = url.toString();
    },
    [isBrowser]
  );

  /**
   * NEW: In-place update without reload (SPA-friendly).
   * opts.push=true uses history.pushState; default is replaceState.
   * Falls back to hard reload if History API throws for any reason.
   */
  const setInPlace = useCallback(
    (updates: Updates = {}, opts?: { push?: boolean }) => {
      if (!isBrowser) return;
      try {
        const url = new URL(window.location.href);
        for (const [k, v] of Object.entries(updates)) {
          if (v === undefined || v === null || v === '') url.searchParams.delete(k);
          else url.searchParams.set(k, String(v));
        }
        const next = url.toString();
        if (opts?.push) window.history.pushState(null, '', next);
        else window.history.replaceState(null, '', next);
      } catch {
        // Fail-safe: hard reload
        set(updates);
      }
    },
    [isBrowser, set]
  );

  /**
   * NEW: In-place remove without reload (SPA-friendly).
   */
  const removeInPlace = useCallback(
    (keys: string[] = [], opts?: { push?: boolean }) => {
      if (!isBrowser) return;
      try {
        const url = new URL(window.location.href);
        for (const k of keys) url.searchParams.delete(k);
        const next = url.toString();
        if (opts?.push) window.history.pushState(null, '', next);
        else window.history.replaceState(null, '', next);
      } catch {
        // Fail-safe: hard reload
        remove(keys);
      }
    },
    [isBrowser, remove]
  );

  return { get, set, remove, setInPlace, removeInPlace };
}

export default useQueryParams;

/**
 * NEW: Tiny helper that behaves like useState but syncs a single query param
 * without reloading (uses replaceState by default).
 */
export function useQueryParamState(key: string, initial = '') {
  const isBrowser = typeof window !== 'undefined';
  const initialFromUrl = useMemo(() => {
    if (!isBrowser) return initial;
    const params = new URLSearchParams(window.location.search);
    return (params.get(key) ?? initial) as string;
  }, [isBrowser, key, initial]);

  const [value, setValue] = useState<string>(initialFromUrl);

  useEffect(() => {
    if (!isBrowser) return;
    try {
      const url = new URL(window.location.href);
      if (value === '' || value == null) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
      window.history.replaceState(null, '', url.toString());
    } catch {
      // Last resort: ignore; don’t crash UI for URL sync
    }
  }, [isBrowser, key, value]);

  return [value, setValue] as const;
}
